// src/pages/TripImportReview.js
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { db } from "../../firebase/firebase";
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  writeBatch,
  getDocs,
} from "firebase/firestore";

import { useQueryParams } from "../../hooks/useQueryParams";
import { mapCsvRow } from "../../utils/tripImportMapper";
import { priceKOI } from "../../utils/tripPricingEngine";
import { geocodeAddress } from "../../utils/geocodeAddress";
import {
  resolveManualTripDriver,
} from "../../utils/manualDriverMapping";


/* --------------- Component --------------- */

export default function TripImportReview() {
  console.log("📌 TripImportReview RENDERED");
  const q = useQueryParams();
  const [clientId, setClientId] = useState("");
  const [batchDate, setBatchDate] = useState("");
  const [preview, setPreview] = useState([]); // mapped rows (readonly preview)
  const [processing, setProcessing] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [driversList, setDriversList] = useState([]);
  const [unmappedDrivers, setUnmappedDrivers] = useState([]);

function applyDriverMapping() {
  // 1️⃣ Update preview list
  setPreview(prev =>
    prev.map(row => {
      const mapped = unmappedDrivers.find(
        u => u.normalizedName === row.normalizedName
      );
      return mapped
        ? {
            ...row,
            firestoreDriverId: mapped.firestoreDriverId,
            firestoreDriverName: mapped.firestoreDriverName,
            isDriverMapped: true,
          }
        : row;
    })
  );

  // 2️⃣ Clear unmapped list
  setUnmappedDrivers([]);

  alert("✅ Driver mapping applied to preview!");
}

  useEffect(() => {
  async function loadDrivers() {
    const snap = await getDocs(collection(db, "drivers"));

    const list = snap.docs.map(d => ({
      id: d.id,
      fullName: d.data().fullName || "",
    }));
    setDriversList(list);
  }
  loadDrivers();
}, []);


  // Prefill from URL ?client=koi&date=2025-11-10
  useEffect(() => {
    const c = (q.get("client") || "").toLowerCase().replace(/ /g, "_");
    if (c) setClientId(c);
    const d = q.get("date") || "";
    if (d) setBatchDate(d);
  }, [q]);

  // ✅ Load all clients from Firestore dynamically
  useEffect(() => {
    async function loadClients() {
      try {
        const snap = await getDocs(collection(db, "clients"));
        const names = snap.docs.map((d) => ({
          id: d.id,
          name: d.data().name,
        }));
        setClientList(names);
      } catch (err) {
        console.error("Error loading clients:", err);
      }
    }
    loadClients();
  }, []);

  async function handleFile(e) {
    console.log("📌 handleFile STARTED");
    const file = e.target.files?.[0];
    if (!file) return;

    setProcessing(true);
    try {
      const ab = await file.arrayBuffer();
const wb = XLSX.read(ab, { type: "array" });
const ws = wb.Sheets[wb.SheetNames[0]];

// ⛔ No cellDates, no dateNF → STOP Excel from converting
const rawRows = XLSX.utils.sheet_to_json(ws, { 
  raw: true, // Get raw values, let OUR parser handle dates
  defval: "" 
});

// ⭐ 100% safe DD/MM/YYYY parser
// ✅ CORRECT DATE PARSER - Replace your entire parseExcelDate function
function parseExcelDate(value) {
  if (!value || value === "") return "";
  
  console.log("🔍 parseExcelDate input:", value, "Type:", typeof value);
  
  // CASE 1: Already a Date object (Excel converted it)
  if (value instanceof Date) {
    const dateStr = value.toISOString().slice(0, 10);
    console.log("✅ Already Date object →", dateStr);
    return dateStr; // "2025-11-01"
  }
  
  // CASE 2: Number (Excel serial date like 45678.12345)
  if (typeof value === 'number') {
    console.log("🔢 Excel serial number:", value);
    // Excel's epoch is 1900-01-00 (yes, day 00!)
    const excelEpoch = new Date(1899, 11, 30); // 1900-01-00 = Dec 30, 1899 in JS
    const jsDate = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
    const dateStr = jsDate.toISOString().slice(0, 10);
    console.log("✅ Serial converted →", dateStr);
    return dateStr;
  }
  
  // CASE 3: String with slashes (your format)
  if (typeof value === 'string') {
    // Remove any time part
    const dateOnly = value.split(' ')[0].trim();
    
    // If already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      console.log("✅ Already YYYY-MM-DD →", dateOnly);
      return dateOnly;
    }
    
    // If DD/MM/YYYY
    if (dateOnly.includes('/')) {
      const parts = dateOnly.split('/');
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        let year = parts[2];
        
        // Ensure 4-digit year
        if (year.length === 2) {
          year = `20${year}`;
        }
        
        const dateStr = `${year}-${month}-${day}`;
        console.log("✅ DD/MM/YYYY parsed →", dateStr);
        return dateStr;
      }
    }
  }
  
  // If we get here, return as-is
  console.warn("⚠ Could not parse date, returning raw:", value);
  return value;
}
// ✅ ADD THIS CRITICAL SECTION: Parse and map the rows
console.log("✅ ADD DIAGNOSTIC TEST");
if (rawRows.length > 0) {
  const sample = rawRows[0];
  console.log("🔍 First row pickupDate raw:", sample.pickupDate);
  console.log("🔍 Type:", typeof sample.pickupDate);
  console.log("🔍 Is Date?", sample.pickupDate instanceof Date);
}

// ⭐ Apply parser to mapped rows
const mapped = rawRows.map(r => {
  const row = mapCsvRow(r);
  
  // DEBUG: Log before parsing
  console.log("📅 BEFORE parsing - pickupDate:", row.pickupDate, "dropDate:", row.dropDate);
  
  row.pickupDate = parseExcelDate(row.pickupDate);
  row.dropDate = parseExcelDate(row.dropDate);
  
  // DEBUG: Log after parsing
  console.log("📅 AFTER parsing - pickupDate:", row.pickupDate, "dropDate:", row.dropDate);
  console.log("---"); // Separator for readability
  
  return row;
});

console.log("✅ Mapped array created with", mapped.length, "rows");

console.log("🔁 Applying driver auto-mapping...")
// STEP 1 — AUTO MAP DRIVERS USING UNIFIED SYSTEM ONLY
for (let r of mapped) {
  const result = await resolveManualTripDriver(r, driversList);

  r.firestoreDriverId = result.driverId || null;
  r.firestoreDriverName = result.driverName || r.driver;
  r.isDriverMapped = result.isMapped;
  r.normalizedName = result.nameNorm;
}

// ❗️STEP 3 — STOP IF ANY DRIVER STILL UNMAPPED
let stillUnmapped = mapped
  .filter(r => !r.isDriverMapped)
  .map((r, idx) => ({
    ...r,
    _uid: `${idx}-${Date.now()}-${Math.random()}`  // 🔥 UNIQUE row identity
  }));

if (stillUnmapped.length > 0) {
  console.warn("❌ Unmapped Drivers:", stillUnmapped);

  // Store unmapped list with unique row identity
  setUnmappedDrivers(stillUnmapped);

  alert(`⚠ ${stillUnmapped.length} unmapped drivers found.\nPlease map below.`);
}



      console.log("📌 Starting GEOCODE LOOP. Total rows:", mapped.length);
      // ⭐ Geocode both pickup & drop (cached)
for (let r of mapped) {
  console.log("💰 KOI CHECK FOR:", r.pickup, "→", r.drop);
  const pickupGeo = await geocodeAddress(r.pickup);
  const dropGeo   = await geocodeAddress(r.drop);

  r.pickupLat = pickupGeo.lat;
  r.pickupLng = pickupGeo.lng;
  r.dropLat = dropGeo.lat;
  r.dropLng = dropGeo.lng;
}



      // If date missing, try to guess from first row's pickupDate serial/text
      if (!batchDate && mapped[0]?.pickupDate) {
        setBatchDate(mapped[0].pickupDate);
      }
      // If client missing, try from first row clientName
      if (!clientId && mapped[0]?.clientName) {
        setClientId(mapped[0].clientName.toLowerCase().replace(/ /g, "_"));
      }
      console.log("📌 Starting KOI PRICE CHECK LOOP");
// ---- After mapped = raw.map(mapCsvRow) ----
for (let r of mapped) {
  const isKOI = (r.clientName || "").toLowerCase().includes("koi");

  if (!isKOI) {
    r.koiExpectedPrice = 0;
    r.koiActualPrice = Number(r.clientFinalPrice) || 0;
    r.priceStatus = "N/A";
    continue;
  }

  // 🔍 DEBUG GEO BEFORE PRICING
  console.log("DEBUG GEO:", {
    pickup: r.pickup,
    pickupLat: r.pickupLat,
    pickupLng: r.pickupLng,
    drop: r.drop,
    dropLat: r.dropLat,
    dropLng: r.dropLng
  });

  const expected = priceKOI({
    pickup: r.pickup,
    drop: r.drop,
    pickupLat: r.pickupLat,
    pickupLng: r.pickupLng,
    dropLat: r.dropLat,
    dropLng: r.dropLng,
    carType: r.carType,
    cfg: {
      rules: {
        koiRates: {
          airportWithinBoundary: { standard: 78, executive: 98 },
          airportBeyondBoundary: { standard: 93, executive: 113 },
          abuDhabi: { standard: 248, executive: 268 },
        },
        distanceThresholdKm: 20
      },
    },
  });



  r.koiExpectedPrice = expected;
  r.koiActualPrice = Number(r.clientFinalPrice) || 0;
  r.priceMismatch = expected !== r.koiActualPrice;
  r.priceDiff = r.koiActualPrice - expected;

  // Assign status
  if (expected === 0) {
    r.priceStatus = "⚠ UNKNOWN (No airport / No rule match)";
  } else if (expected !== r.koiActualPrice) {
    r.priceStatus = "❌ WRONG PRICE";
  } else {
    r.priceStatus = "✔ OK";
  }
}


      setPreview(mapped);
    } catch (err) {
      console.error(err);
      alert("Failed to read file. Please check the format.");
    } finally {
      setProcessing(false);
      e.target.value = "";   // ⭐ allow same file upload again
    }
  }


  // ✅ Optimized & User-Friendly Version
  async function saveAsDraft() {
    try {
      if (!preview.length)
        return alert("No rows to save. Upload a file first.");
      if (!clientId) return alert("Select Client.");
      if (!batchDate)
        return alert("Select Batch Date (YYYY-MM-DD).");

      // Start progress
      setProcessing(true);

      // ✅ Define batchKey
      const batchKey = `${clientId}_${batchDate}`;
      console.log(
        "💾 Saving draft for:",
        batchKey,
        "rows:",
        preview.length
      );

      // ✅ Step 1 — Create or update batch document
      const batchDocRef = doc(db, "tripImports", batchKey);
      await setDoc(
        batchDocRef,
        {
          clientId,
          batchDate,
          status: "uploaded",
          createdAt: serverTimestamp(),
          totals: { rows: preview.length },
        },
        { merge: true }
      );
      console.log("✅ Batch document created");

      // ✅ Step 2 — Write trips using batches (up to 400 per commit)
      const chunkSize = 400;
      let totalSaved = 0;

      for (let i = 0; i < preview.length; i += chunkSize) {
        const batch = writeBatch(db);
        const slice = preview.slice(i, i + chunkSize);

        slice.forEach((r) => {
          const docRef = doc(collection(batchDocRef, "trips"));
          const normalizedClientId = (r.clientName || clientId)
            .toLowerCase()
            .replace(/ /g, "_");

         batch.set(docRef, {
  ...r,

  // --- NEW DRIVER MAPPING FIELDS ---
  firestoreDriverId: r.firestoreDriverId || null,
  driverId: r.firestoreDriverId || null,
firestoreDriverName: r.firestoreDriverName || null,
isDriverMapped: r.isDriverMapped || false,


  // Keep driver original name as well for history
  originalDriverName: r.driver || null,

  // --- LOCATION FIELDS ---
  pickupLat: r.pickupLat || null,
  pickupLng: r.pickupLng || null,
  dropLat: r.dropLat || null,
  dropLng: r.dropLng || null,

  // --- CLIENT ---
  clientId: normalizedClientId,

  // --- DATES ---
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});


        });

        await batch.commit();
        totalSaved += slice.length;
        console.log(
          `✅ Batch ${i / chunkSize + 1} saved (${slice.length} trips)`
        );

        // ✅ Update progress bar or message (optional)
        const percent = Math.round(
          (totalSaved / preview.length) * 100
        );
        const msg = `Saving progress: ${percent}% (${totalSaved}/${preview.length})`;
        console.log(msg);
        // Optionally show progress on screen
        document.title = msg;
      }

      // ✅ Step 3 — Completion
      alert(`✅ Saved ${preview.length} trips successfully as DRAFT.`);
      console.log("🎉 All trips saved successfully!");
      setPreview([]);
    } catch (err) {
      console.error("🔥 Error while saving draft:", err);
      alert("❌ Failed to save draft. Check console for details.");
    } finally {
      setProcessing(false);
    }
  }

  async function handleClientSelect(e) {
    const value = e.target.value;

    if (value === "add_new") {
      const newClient = prompt("Enter new client name:");
      if (newClient) {
        const id = newClient.toLowerCase().replace(/ /g, "_");
        await setDoc(doc(db, "clients", id), {
          name: newClient,
          createdAt: serverTimestamp(),
        });
        setClientList((prev) => [...prev, { id, name: newClient }]);
        setClientId(id);
      }
    } else {
      setClientId(value);
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>📥 Upload Daily Trips (Save as Draft)</h2>

      {/* Top controls: Client + Date */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label>Client:</label>
        <select value={clientId} onChange={handleClientSelect}>
          <option value="">Select client</option>
          {clientList.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
          <option value="add_new">➕ Add New Client</option>
        </select>

        <label style={{ marginLeft: 12 }}>Batch Date:</label>
        <input
          type="date"
          value={batchDate}
          onChange={(e) => setBatchDate(e.target.value)}
        />
      </div>

      {/* File upload */}
      <input
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleFile}
        style={{ marginTop: 6 }}
      />

      {processing && <p>⏳ Please Wait Data is Uploading…</p>}
    {unmappedDrivers.length > 0 && (
  <div style={{ marginTop: 20, padding: 15, border: "1px solid red", borderRadius: 6 }}>
    <h4>🚨 Unmapped Drivers Found</h4>

    {unmappedDrivers.map((r, index) => (
      <div key={index} style={{ marginBottom: 10 }}>
        <b>{r.driver}</b>

        <select
  onChange={(e) => {
    const selected = driversList.find(d => d.id === e.target.value);

    setUnmappedDrivers(prev =>
      prev.map(row =>
        row.normalizedName === r.normalizedName
          ? {
              ...row,
              firestoreDriverId: selected?.id || null,
              firestoreDriverName: selected?.fullName || row.driver,
              isDriverMapped: true,
            }
          : row
      )
    );
  }}
>

          <option value="">Select Driver</option>
          {driversList.map(d => (
            <option value={d.id} key={d.id}>
              {d.fullName}
            </option>
          ))}
        </select>
      </div>
    ))}
  </div>
)}
      {/* Preview */}
      {preview.length > 0 && (
        <>
          <p style={{ marginTop: 16 }}>
            Previewing <b>{preview.length}</b> rows — this is a
            read-only preview. Click <b>“Save as Draft”</b> to store
            daily trips for other teams to update later.
          </p>

          <div
            style={{
              maxHeight: 420,
              overflow: "auto",
              border: "1px solid #eee",
            }}
          >
            <table
              border="1"
              cellPadding="6"
              style={{ background: "#fff", width: "100%" }}
            >
              <thead>
                <tr>
                  <th>Trip ID</th>
                  <th>Passenger</th>
                  <th>Driver</th>
                  <th>Pickup</th>
                  <th>Drop</th>
                  <th>Car</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>KOI Price Check</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i}>
                    <td>{r.reservationId}</td>
                    <td>{r.passengerName}</td>
                    <td>
  {r.firestoreDriverName}
  {!r.isDriverMapped && (
    <span style={{ color: "red", fontWeight: "bold" }}>
      {" "}
      (Unmapped)
    </span>
  )}
</td>

                    <td>{r.pickup}</td>
                    <td>{r.drop}</td>
                    <td>{r.carType}</td>
                    <td>{r.pickupDate}</td>
                    <td>{r.pickupTime}</td>
                    <td>{r.clientName}</td>
                    <td>{r.status}</td>
                    {/* ⭐ NEW PRICE CHECK CELL */}
<td
  style={{
    background:
      r.priceStatus === "❌ WRONG PRICE"
        ? "#ffb3b3"
        : r.priceStatus?.startsWith("⚠")
        ? "#fff3cd"
        : "#d4edda",
    fontWeight: 600,
    minWidth: "150px",
  }}
>
  {r.koiExpectedPrice > 0 ? (
    <>
      Expected: {r.koiExpectedPrice} <br />
      Imported: {r.koiActualPrice} <br />
      <b>{r.priceStatus}</b>
    </>
  ) : (
    r.priceStatus
  )}
</td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
{unmappedDrivers.length > 0 && (
  <button
    onClick={applyDriverMapping}
    style={{
      marginTop: 15,
      padding: "8px 14px",
      background: "#0d6efd",
      color: "#fff",
      border: "none",
      borderRadius: 5,
      cursor: "pointer",
      fontWeight: "bold"
    }}
  >
    ✅ Apply Driver Mapping
  </button>
)}

          <button
            onClick={saveAsDraft}
            style={{
              marginTop: 16,
              padding: "10px 16px",
              background: "#0066cc",
              color: "#fff",
              borderRadius: 6,
            }}
          >
            💾 Save as Draft
          </button>
        </>
      )}
    </div>
  );
}
